import { useEffect, type MutableRefObject } from "react";
import {
  useContentShareState,
  useLocalVideo,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { VideoPriorityBasedPolicy } from "amazon-chime-sdk-js";
import { Layout, updateDownlinkPreferences } from "@core/features/video";
import type { VideoSourceState } from "@core/features/video";
import { usePlatformStore, useVideoConfig } from "@platform/store";

/**
 * Syncs local video state (`isVideoEnabled`, `isLocalUserSharing`,
 * `sharingAttendeeId`) into Zustand and updates downlink preferences.
 * Auto-switches to Featured layout on content share start.
 */
export function useLocalVideoSync(
  policyRef: MutableRefObject<VideoPriorityBasedPolicy>
) {
  const meetingManager = useMeetingManager();
  const { isVideoEnabled } = useLocalVideo();
  const { isLocalUserSharing, sharingAttendeeId } = useContentShareState();
  const { layout, setLayout } = useVideoConfig();

  // Sync local video / content share into Zustand + downlink preferences
  useEffect(() => {
    const s = usePlatformStore.getState();

    const nextVideoSourceState: VideoSourceState = {
      ...s.videoSourceState,
      hasLocalVideo: isVideoEnabled,
      hasLocalContentSharing: isLocalUserSharing,
      contentShareId: sharingAttendeeId,
    };

    s.setVideoSourceState(nextVideoSourceState);
    updateDownlinkPreferences(
      { layout: s.layout },
      nextVideoSourceState,
      s.attendeeTileState,
      policyRef.current
    );
  }, [isLocalUserSharing, isVideoEnabled, meetingManager.meetingSession, sharingAttendeeId]);

  // Sync layout changes into downlink preferences
  useEffect(() => {
    const s = usePlatformStore.getState();
    updateDownlinkPreferences(
      { layout },
      s.videoSourceState,
      s.attendeeTileState,
      policyRef.current
    );
  }, [layout]);

  // Auto-switch to Featured layout on content share
  useEffect(() => {
    if (sharingAttendeeId && layout === Layout.Gallery) {
      setLayout(Layout.Featured);
    }
  }, [sharingAttendeeId]);
}
